import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Box, Flex } from '@chakra-ui/react'
import { OrderPaymentStatus } from '@leid/api-orders'
import Router from 'next/router'

import { OrderInfo } from 'appTypes/orders'
import { LeidButtonWhiteOutlined, LeidYellowButton } from 'components/ui/Buttons/Button'
import { Loader } from 'components/ui/Loader'
import apiClient from 'helpers/clients/apiClient'
import { useCustomerInfo } from 'hooks/useCustomerInfo'
import { resetCart } from 'store/partials/cart/cartSlice'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'
import { STORAGE_KEYS } from 'utils/constants'
import { getPaymentSelectPageUrl } from 'utils/redirectUrlsBuilders'
import { saveToStorage } from 'utils/storage'

type Props = {
  orderInfo: OrderInfo
  refetchOrder: () => void
}

const ContinuePaymentButtons = ({ orderInfo, refetchOrder }: Props): JSX.Element => {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()
  const { customerInfo } = useCustomerInfo()

  const [disabledCancelOrderButton, setDisabledCancelOrderButton] = useState<boolean>(false)

  const redirectToContinuePayment = () => {
    saveToStorage(STORAGE_KEYS.orderId, orderInfo.order.id)

    const purchaseAs = customerInfo.organization ? 'organization' : 'person'

    Router.push(getPaymentSelectPageUrl(orderInfo.order.id, undefined, purchaseAs))
  }

  const cancelOrder = async () => {
    setDisabledCancelOrderButton(true)

    try {
      const cancelResponse = await apiClient.cancelOrder(orderInfo.order.id)
      if (!cancelResponse.success || !cancelResponse.data) {
        dispatch(displaySnackbar({ message: t('error'), isError: true }))
        return
      }

      dispatch(displaySnackbar({ message: t('orderWasCanceled'), isError: false }))
      dispatch(resetCart())

      refetchOrder()
    } finally {
      setDisabledCancelOrderButton(false)
    }
  }

  return (
    <Box width="100%">
      {orderInfo.order.status === OrderPaymentStatus.paymentStarted &&
        orderInfo.orderItems.every((s) => s.prolongations.length === 0) && (
          <>
            <Flex justifyContent="center" alignItems="center" flexDirection="column" gridGap="12px">
              <LeidYellowButton onClick={redirectToContinuePayment}>{t('continuePayment')}</LeidYellowButton>

              <LeidButtonWhiteOutlined onClick={cancelOrder} isDisabled={disabledCancelOrderButton}>
                {disabledCancelOrderButton ? <Loader margin={0} size="lg" /> : t('cancelOrder')}
              </LeidButtonWhiteOutlined>
            </Flex>
          </>
        )}
    </Box>
  )
}

export default ContinuePaymentButtons
